import { useEffect } from "react";
import { useLanguage } from "@/lib/i18n";
import { translations } from "@/lib/translations";
import { ogDefaultFor } from "@/lib/seo";

const SITE_URL = "https://refoundapp.ca";
const LOGO_URL = `${SITE_URL}/icon.png`;

function upsertMeta(attr: "name" | "property", value: string, content: string) {
  const selector = `meta[${attr}="${value}"]`;
  let el = document.head.querySelector<HTMLMetaElement>(selector);
  if (!el) {
    el = document.createElement("meta");
    el.setAttribute(attr, value);
    document.head.appendChild(el);
  }
  el.setAttribute("content", content);
}

function upsertLink(rel: string, href: string, hreflang?: string) {
  const selector = hreflang
    ? `link[rel="${rel}"][hreflang="${hreflang}"]`
    : `link[rel="${rel}"]:not([hreflang])`;
  let el = document.head.querySelector<HTMLLinkElement>(selector);
  if (!el) {
    el = document.createElement("link");
    el.setAttribute("rel", rel);
    if (hreflang) el.setAttribute("hreflang", hreflang);
    document.head.appendChild(el);
  }
  el.setAttribute("href", href);
}

function upsertJsonLd(id: string, data: object) {
  const selector = `script[data-seo-id="${id}"]`;
  let el = document.head.querySelector<HTMLScriptElement>(selector);
  if (!el) {
    el = document.createElement("script");
    el.setAttribute("type", "application/ld+json");
    el.setAttribute("data-seo-id", id);
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

export function SupportSEO() {
  const { language } = useLanguage();

  useEffect(() => {
    const isFr = language === "fr";
    const OG_IMAGE = ogDefaultFor(language);
    const htmlLang = isFr ? "fr-CA" : "en-CA";
    const path = isFr ? "/support" : "/en/support";
    const url = `${SITE_URL}${path}`;
    const urlFr = `${SITE_URL}/support`;
    const urlEn = `${SITE_URL}/en/support`;

    const title = isFr
      ? "Aide & FAQ — ReFound | Support des reçus, garanties et retours"
      : "Help & FAQ — ReFound | Receipt, warranty and return support";

    const description = isFr
      ? "Questions fréquentes sur ReFound : détaillants pris en charge, sécurité des données, numérisation des reçus, garanties, retours en magasin et confidentialité."
      : "Answers to the most common questions about ReFound: supported retailers, data security, receipt scanning, warranty tracking, in-store returns and privacy.";

    const keywords = isFr
      ? "ReFound aide, FAQ ReFound, support reçu, détaillants pris en charge, sécurité données reçu, numérisation reçu Canada, suivi garantie, retour magasin Canada"
      : "ReFound help, ReFound FAQ, receipt support, supported retailers, receipt data security, receipt scanning Canada, warranty tracking, in-store returns Canada";

    document.title = title;
    document.documentElement.setAttribute("lang", htmlLang);

    // Standard meta
    upsertMeta("name", "description", description);
    upsertMeta("name", "keywords", keywords);
    upsertMeta("name", "author", "ReFound");
    upsertMeta(
      "name",
      "robots",
      "index, follow, max-image-preview:large, max-snippet:-1",
    );

    // Open Graph
    upsertMeta("property", "og:type", "website");
    upsertMeta("property", "og:title", title);
    upsertMeta("property", "og:description", description);
    upsertMeta("property", "og:url", url);
    upsertMeta("property", "og:image", OG_IMAGE);
    upsertMeta("property", "og:image:alt", "ReFound — Aide & FAQ");
    upsertMeta("property", "og:locale", isFr ? "fr_CA" : "en_CA");
    upsertMeta("property", "og:locale:alternate", isFr ? "en_CA" : "fr_CA");
    upsertMeta("property", "og:site_name", "ReFound");

    // Twitter
    upsertMeta("name", "twitter:card", "summary_large_image");
    upsertMeta("name", "twitter:title", title);
    upsertMeta("name", "twitter:description", description);
    upsertMeta("name", "twitter:image", OG_IMAGE);

    // Canonical + hreflang
    upsertLink("canonical", url);
    upsertLink("alternate", urlFr, "fr-CA");
    upsertLink("alternate", urlEn, "en-CA");
    upsertLink("alternate", urlFr, "x-default");

    // -------- JSON-LD --------

    const supportT = (isFr ? translations.fr : translations.en).support;

    // 1) FAQPage — primary AEO/GEO signal
    const faqPage = {
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "@id": `${url}#faq`,
      inLanguage: htmlLang,
      url,
      name: supportT.faq.title,
      isPartOf: { "@id": `${SITE_URL}#website` },
      about: { "@id": `${SITE_URL}#app` },
      mainEntity: supportT.faq.questions.map((q) => ({
        "@type": "Question",
        name: q.question,
        acceptedAnswer: {
          "@type": "Answer",
          text: q.answer,
        },
      })),
    };

    // 2) ContactPage (email support + feedback)
    const contactPage = {
      "@context": "https://schema.org",
      "@type": "ContactPage",
      "@id": `${url}#contact`,
      url,
      name: supportT.title,
      description: supportT.subtitle,
      inLanguage: htmlLang,
      isPartOf: { "@id": `${SITE_URL}#website` },
      mainEntity: {
        "@type": "Organization",
        "@id": `${SITE_URL}#organization`,
        name: "ReFound",
        url: SITE_URL,
        logo: { "@type": "ImageObject", url: LOGO_URL },
        email: supportT.emailSupport.email,
        contactPoint: [
          {
            "@type": "ContactPoint",
            contactType: "customer support",
            email: supportT.emailSupport.email,
            availableLanguage: ["French", "English"],
            areaServed: "CA",
          },
        ],
      },
    };

    // 3) BreadcrumbList
    const breadcrumb = {
      "@context": "https://schema.org",
      "@type": "BreadcrumbList",
      itemListElement: [
        {
          "@type": "ListItem",
          position: 1,
          name: isFr ? "Accueil" : "Home",
          item: isFr ? `${SITE_URL}/` : `${SITE_URL}/en`,
        },
        {
          "@type": "ListItem",
          position: 2,
          name: isFr ? "Aide" : "Help",
          item: url,
        },
      ],
    };

    upsertJsonLd("support-faq", faqPage);
    upsertJsonLd("support-contact", contactPage);
    upsertJsonLd("support-breadcrumb", breadcrumb);
  }, [language]);

  return null;
}
